<template>
  <div class="stepper-wrapper">
    <div class="stepper-container">
      <div class="steps-row">
        <div 
          v-for="(step, index) in steps" 
          :key="step.id"
          class="step-item"
          @click="onStepClick(step.id)"
          :class="getStepClasses(step)"
        >
          <!-- Step Circle -->
          <div class="step-circle">
            <!-- Show checkmark for completed steps -->
            <i 
              v-if="isCompleted(step.id)"
              class="bi bi-check step-check"
            ></i>
            <!-- Show step number for non-completed steps -->
            <span 
              v-else
              class="step-number"
            >
              {{ step.id }}
            </span>
          </div>
          
          <!-- Step Title -->
          <div class="step-title">
            {{ step.title }}
          </div>
          
          <!-- Connecting line (except for last step) -->
          <div 
            v-if="index < steps.length - 1" 
            class="step-line"
            :class="{ 'completed': isCompleted(step.id) }"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StepperComponent',
  props: {
    steps: {
      type: Array,
      required: true
    },
    currentStep: {
      type: Number,
      required: true
    },
    completedSteps: {
      type: Array,
      default: () => []
    }
  },
  methods: {
    onStepClick(stepId) {
      this.$emit('step-change', stepId)
    },
    
    getStepClasses(step) {
      return {
        'active': step.id === this.currentStep,
        'completed': this.isCompleted(step.id),
        'clickable': true
      }
    },
    
    isCompleted(stepId) {
      return this.completedSteps.includes(stepId)
    }
  }
}
</script>

<style scoped>
.stepper-wrapper {
  background: #f8f9fa;
  padding: 2rem 0;
  margin: 0;
}

.stepper-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

.steps-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  position: relative;
  width: 100%;
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  cursor: pointer;
  flex: 1;
  max-width: 200px;
}

.step-circle {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #ffffff;
  border: 2px solid #d1d5db;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem;
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
}

/* Active Step (Purple) */
.step-item.active .step-circle {
  background-color: #8b5cf6;
  border-color: #8b5cf6;
  color: white;
}

/* Completed Step (Purple with white checkmark) */
.step-item.completed .step-circle {
  background-color: #8b5cf6;
  border-color: #8b5cf6;
  color: white;
}

.step-number {
  font-size: 18px;
  font-weight: 600;
  color: #6b7280;
}

.step-item.active .step-number,
.step-item.completed .step-number {
  color: white;
}

.step-check {
  font-size: 20px;
  font-weight: bold;
  color: white;
}

.step-title {
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  text-align: center;
  max-width: 120px;
  line-height: 1.3;
  word-wrap: break-word;
}

/* Active step title */
.step-item.active .step-title {
  color: #8b5cf6;
  font-weight: 600;
}

/* Completed step title */
.step-item.completed .step-title {
  color: #6b7280;
  font-weight: 500;
}

/* Connecting Lines */
.step-line {
  position: absolute;
  top: 25px;
  left: calc(50% + 25px);
  width: calc(100% - 50px);
  height: 2px;
  background-color: #d1d5db;
  z-index: 1;
  transition: all 0.3s ease;
}

.step-line.completed {
  background-color: #8b5cf6;
}

/* Hover Effects */
.step-item.clickable:hover .step-circle {
  transform: scale(1.05);
  box-shadow: 0 2px 8px rgba(139, 92, 246, 0.2);
}

/* Responsive Design */
@media (max-width: 1024px) {
  .stepper-container {
    padding: 0 1rem;
  }
  
  .step-circle {
    width: 45px;
    height: 45px;
  }
  
  .step-number {
    font-size: 16px;
  }
  
  .step-check {
    font-size: 18px;
  }
  
  .step-title {
    font-size: 12px;
    max-width: 100px;
  }
}

@media (max-width: 768px) {
  .stepper-wrapper {
    padding: 1.5rem 0;
  }
  
  .steps-row {
    flex-wrap: wrap;
    justify-content: center;
    gap: 1.5rem;
  }
  
  .step-item {
    flex-direction: row;
    align-items: center;
    max-width: none;
    width: 100%;
    max-width: 300px;
  }
  
  .step-circle {
    margin-bottom: 0;
    margin-right: 1rem;
    flex-shrink: 0;
  }
  
  .step-title {
    text-align: left;
    max-width: none;
    flex: 1;
    font-size: 14px;
  }
  
  .step-line {
    display: none;
  }
}

@media (max-width: 480px) {
  .step-circle {
    width: 40px;
    height: 40px;
  }
  
  .step-number {
    font-size: 14px;
  }
  
  .step-check {
    font-size: 16px;
  }
  
  .step-title {
    font-size: 13px;
  }
}
</style>